// Section 9: housing is the shelter need's supply. Beds are assigned by household at the start of
// the season (decide phase, ADR-0010 item 21): a household needs beds for all its members in one
// building. Households are placed in housing-policy order (Section 18): each keeps its current
// building if it still fits, otherwise takes the building with the fewest free beds that fits
// (ties by id). When nothing fits, the policy may split the household across buildings with free
// beds (B-07), then squeeze it into the building with most free beds if overcrowding is allowed;
// otherwise its members sleep rough. In an overcrowded building the beds are shared out in
// proportion to household size (largest remainder, ties by id); each household's "extra" people
// are its members without a bed (Section 12 crowding, D-12). A ruined building (condition 0)
// has no beds (B-06).
import { config as defaultConfig, type Config } from '../../config';
import { isChild } from '../../core/queries';
import { sortedValues } from '../../core/sorted';
import type { BuildingId, GameState, Household, HouseholdId } from '../../core/types';
import type { Registry } from '../../core/registry';

export interface HousingResult {
  /** The building each household sleeps in (the one holding most of a split household). */
  assignment: Record<HouseholdId, BuildingId | null>;
  /** Members without a bed per household. */
  crowding: Record<HouseholdId, number>;
  /** Households split across buildings: building -> members placed there. */
  split: Record<HouseholdId, Partial<Record<BuildingId, number>>>;
}

/** Beds a building offers: its type's beds while it stands (condition above 0). */
export function bedsOf(registry: Registry, state: GameState, id: BuildingId): number {
  const b = state.buildings[id];
  if (b === undefined || b.condition <= 0) return 0;
  return registry.buildings[b.type].beds;
}

/** Total beds in the settlement (Section 9: the shelter need's supply). */
export function totalBeds(registry: Registry, state: GameState): number {
  let beds = 0;
  for (const b of sortedValues(state.buildings)) beds += bedsOf(registry, state, b.id);
  return beds;
}

const byId = (a: Household, b: Household): number => (a.id < b.id ? -1 : 1);

function orderHouseholds(state: GameState, cfg: Config, households: Household[]): Household[] {
  const workers = (h: Household): number =>
    h.memberIds.filter((id) => {
      const job = state.people[id]?.job;
      return job !== undefined && job !== 'rest';
    }).length;
  const hasChildren = (h: Household): number =>
    h.memberIds.some((id) => {
      const p = state.people[id];
      return p !== undefined && isChild(cfg, p);
    })
      ? 1
      : 0;
  const size = (h: Household): number => h.memberIds.length;
  switch (state.policies.housing.mode) {
    case 'families-first':
      // Households with children first, then larger households.
      return households.sort(
        (a, b) => hasChildren(b) - hasChildren(a) || size(b) - size(a) || byId(a, b),
      );
    case 'workers-first':
      return households.sort((a, b) => workers(b) - workers(a) || size(b) - size(a) || byId(a, b));
    case 'first-come':
      // Household ids are minted in order of founding or arrival.
      return households.sort(byId);
    case 'manual':
      // Keep who is housed where: the housed first (by id), then the rest in arrival order.
      return households.sort(
        (a, b) => Number(a.buildingId === null) - Number(b.buildingId === null) || byId(a, b),
      );
  }
}

/** Share `beds` among households in proportion to their size (largest remainder, ties by id). */
function shareBeds(
  households: Household[],
  beds: number,
  crowding: Record<HouseholdId, number>,
): void {
  const occupants = households.reduce((n, h) => n + h.memberIds.length, 0);
  if (occupants <= beds) return;
  const rows = households.map((h) => {
    const exact = (h.memberIds.length * beds) / occupants;
    return { h, beds: Math.floor(exact), rest: exact - Math.floor(exact) };
  });
  let left = beds - rows.reduce((n, r) => n + r.beds, 0);
  for (const row of rows.slice().sort((a, b) => b.rest - a.rest || byId(a.h, b.h))) {
    if (left <= 0) break;
    row.beds += 1;
    left -= 1;
  }
  for (const row of rows) {
    const extra = row.h.memberIds.length - row.beds;
    if (extra > 0) crowding[row.h.id] = extra;
  }
}

/**
 * `cfg` defaults to the module config for M1 callers (decide passes only state and registry);
 * it is read only for the child age in families-first ordering.
 */
export function assignHousing(
  state: GameState,
  registry: Registry,
  cfg: Config = defaultConfig,
): HousingResult {
  const capacity = new Map<BuildingId, number>();
  for (const b of sortedValues(state.buildings)) {
    const beds = bedsOf(registry, state, b.id);
    if (beds > 0) capacity.set(b.id, beds);
  }
  const used = new Map<BuildingId, number>();
  const residents = new Map<BuildingId, Household[]>();
  const free = (id: BuildingId): number => (capacity.get(id) ?? 0) - (used.get(id) ?? 0);
  const assignment: Record<HouseholdId, BuildingId | null> = {};
  const split: Record<HouseholdId, Partial<Record<BuildingId, number>>> = {};
  const policy = state.policies.housing;

  const place = (h: Household, id: BuildingId, members: number): void => {
    used.set(id, (used.get(id) ?? 0) + members);
    if (members === h.memberIds.length) residents.set(id, [...(residents.get(id) ?? []), h]);
  };

  for (const h of orderHouseholds(state, cfg, sortedValues(state.households))) {
    const size = h.memberIds.length;
    if (size === 0) {
      assignment[h.id] = null;
      continue;
    }
    // 1. The current building if it still fits, else the tightest building that fits.
    let chosen: BuildingId | null = null;
    if (h.buildingId !== null && free(h.buildingId) >= size) chosen = h.buildingId;
    if (chosen === null) {
      let best = Number.POSITIVE_INFINITY;
      for (const id of capacity.keys()) {
        const room = free(id);
        if (room >= size && room < best) {
          best = room;
          chosen = id;
        }
      }
    }
    if (chosen !== null) {
      assignment[h.id] = chosen;
      place(h, chosen, size);
      continue;
    }
    // 2. Split across buildings with free beds, most room first (policy option, B-07).
    const rooms = [...capacity.keys()]
      .map((id) => ({ id, room: free(id) }))
      .filter((r) => r.room > 0)
      .sort((a, b) => b.room - a.room || (a.id < b.id ? -1 : 1));
    const roomTotal = rooms.reduce((n, r) => n + r.room, 0);
    if (policy.splitHouseholds === true && roomTotal >= size) {
      let left = size;
      const parts: Partial<Record<BuildingId, number>> = {};
      for (const r of rooms) {
        if (left <= 0) break;
        const n = Math.min(left, r.room);
        parts[r.id] = n;
        place(h, r.id, n);
        left -= n;
      }
      split[h.id] = parts;
      assignment[h.id] = rooms[0]?.id ?? null;
      continue;
    }
    // 3. Squeeze into the building with most free beds, if overcrowding is allowed.
    if (policy.allowOvercrowding) {
      let most = Number.NEGATIVE_INFINITY;
      for (const id of capacity.keys()) {
        if (free(id) > most) {
          most = free(id);
          chosen = id;
        }
      }
    }
    // 4. Otherwise (or with no building at all) the household sleeps rough.
    assignment[h.id] = chosen;
    if (chosen !== null) place(h, chosen, size);
  }
  const crowding: Record<HouseholdId, number> = {};
  for (const [id, households] of residents) {
    // Split households' members in this building take beds first; the rest share what is left.
    const splitHere = sortedValues(split).reduce((n, parts) => n + (parts[id] ?? 0), 0);
    shareBeds(households, (capacity.get(id) ?? 0) - splitHere, crowding);
  }
  return { assignment, crowding, split };
}
